/**
 * Channel Rule Flows API client. Ref: PRISM7_CHANNEL_RULES_CANVAS_TDD.md §1/§4/§5.
 *
 * Two base URLs, matching each call's real owner (TDD §8.2, channelRuleFlowsService.ts row):
 * list/get/confirm are owned and persisted by BE (EMAIL_API_BASE_URL) — mirrors
 * emailService.ts's own fetch/auth pattern. parse/serialize are compute-only ORCH routes
 * (ML_API_BASE_URL) — mirrors workflowConfigService.ts's pattern. Neither ORCH route ever
 * writes gg_channel_rule_flows; BE's /replace (parse's own persistence step) is internal,
 * service-to-service only, and deliberately has no FE-facing wrapper here.
 */
import {
  API_CONFIG,
  CHANNEL_RULE_FLOWS_API_ENDPOINTS,
  CHANNEL_RULES_PARSE_API_ENDPOINTS,
  STORAGE_KEYS,
} from '../helpers/constants';
import { parseErrorDetail } from './apiErrorUtils';

export type RuleFlowNodeType = 'TRIGGER' | 'ACTION' | 'CONDITION';

/** Where a recipient's email came from (TDD §5.1) — governs whether an edit can be a
 * deterministic text substitution (an existing anchor) or needs a scoped LLM fallback. */
export type RuleFlowRecipientSource = 'field_block' | 'inline' | 'missing' | 'user_selected';

export interface RuleFlowRecipient {
  role?: string;
  email: string | null;
  source: RuleFlowRecipientSource;
}

export interface RuleFlowNodeMeta {
  action_kind?: string;
  /** ADR §2/§3 — set only on an ACTION that genuinely needs a human approver. A fallback ACTION
   * reached when no approver could be resolved (e.g. `action_kind: "flag_and_draft"`) never sets
   * this, by design (ADR §3 amendment) — it must never be treated as incomplete. */
  requires_approval?: boolean;
}

export interface RuleFlowGraphNode {
  id: string;
  type: RuleFlowNodeType;
  /** PR #635 review (C1): every producer (parse, serialize) always sends null on the real wire
   * — confirmed in shay-email-ml#603, commented there as "Canvas-only layout hint (React Flow).
   * Round-tripped, never interpreted." No backend ever populates a real {x, y}. Typed as
   * nullable to match that reality; RuleFlowCanvas.tsx computes a fallback layout whenever it
   * sees null (see ruleFlowLayout.ts), rather than trusting this field is always populated. */
  position: { x: number; y: number } | null;
  // Bug found live (webscrapping/Business_rule_canvas branch, this session): label/recipients/
  // meta/source_excerpt were originally modeled as nested under a `data: {...}` sub-object here,
  // mirroring React Flow's own Node.data convention. That convention only applies to the
  // internal React Flow node this service's consumers BUILD (RuleFlowCanvas.tsx's docToRfNodes,
  // typed separately as RuleNodeData in RuleNode.tsx) — it was never part of the real wire
  // contract. ADR §2's own JSON example puts these fields flat on the node, matching BE/ORCH's
  // actual response (confirmed once real backend data finally reached the client and rendered
  // with a blank label — every earlier test had hit a 404/502 before any node data arrived).
  // Fields below are flat to match that reality exactly.
  label: string;
  recipients?: RuleFlowRecipient[];
  /** Anchor into the channel's raw business-rules text (field_block/inline sourced fields only)
   * — presence of an anchor is what lets ORCH's serialize do a deterministic substitution
   * instead of an LLM call (TDD §5.2). Display-only on the frontend; never edited directly. */
  source_excerpt?: string;
  /** ADR §2 — drives the canvas-level "incomplete flow" hard-block (ADR §3, see
   * ruleFlowValidation.ts). Optional because ORCH's parse output for TRIGGER/CONDITION nodes
   * (and any ACTION that doesn't require approval) has no reason to set it. */
  meta?: RuleFlowNodeMeta;
  /** Node-level recommendations architecture proposal — human-readable gap descriptions scoped
   * to THIS node (e.g. "No email for customer support team..."), computed server-side by the
   * same ORCH pass that resolves recipients/meta above. Not sent by any backend yet — this field
   * is added ahead of that so the frontend rendering (RuleNode.tsx) is ready to light up the
   * moment it starts arriving; absent/empty today on every real flow, by design. Display-only,
   * never sent back to the backend. */
  recommendations?: string[];
  /** PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §3.1 — an annotation, not a rule edit: BE persists
   * this alongside the node but it never participates in serialize/business-rules text. Absent on
   * historical rows parsed before this feature; the FE must render those exactly as before. */
  description?: string;
  /** 'generated' for ORCH's batch enrichment pass, 'user' once a human overrides it via PATCH.
   * Absent on historical rows — treat as if no override/generation has happened yet. */
  description_source?: 'generated' | 'user';
  /** Diagnostic only (sha256 of the rules text used to generate `description`) — set only when
   * description_source is 'generated'; never a staleness gate for execution (TDD §3.1). */
  description_rules_hash?: string;
}

export interface RuleFlowGraphEdge {
  id: string;
  source: string;
  target: string;
  condition?: string | null;
}

/** One flow, as returned by BE's list/get (TDD §4.2) — full detail, not a summary; the list
 * response already includes everything the canvas needs to render without a second round trip. */
export interface RuleFlow {
  id: string;
  flow_name: string;
  trigger_condition_text: string;
  field_block_excerpt?: string | null;
  nodes: RuleFlowGraphNode[];
  edges: RuleFlowGraphEdge[];
  updated_at: string;
  /** source_rules_hash (row) != sha256(current channel_business_rules) — computed server-side. */
  is_stale: boolean;
}

export interface ListRuleFlowsResponse {
  flows: RuleFlow[];
}

/** Frontend handover doc §2 (FIX 5/6) — send exactly one of edited_node OR insert_node, never
 * both, never neither; the backend returns 400 otherwise. edited_node's label/text are new
 * (FIX 9b, staged rename/reword edits); insert_node is entirely new (FIX 9a, staged "insert step"
 * edits) — both only fire via FIX 11's Save button, never immediately like a recipient edit. */
export interface SerializeRuleFlowRequest {
  /** Only the one node actually being edited — never the whole graph (TDD §5.2). */
  edited_node?: {
    id: string;
    recipients?: RuleFlowRecipient[];
    label?: string;
    text?: string;
  };
  insert_node?: {
    after_node_id: string;
    before_node_id: string;
    node_type: 'ACTION' | 'CONDITION';
    label: string;
    text: string;
    edge_condition?: string;
  };
  /** FIX 13-FE (delete-node handover doc §2/§3.1) — deletes an existing node; `edited_node`/
   * `insert_node`'s "send exactly one" rule above extends to this: never combined with either. */
  delete_node?: {
    node_id: string;
  };
}

/** Compute-only — never persists (TDD §5.2). */
export interface SerializeRuleFlowResponse {
  updated_flow: RuleFlow;
  /** Pass this into saveChannelBusinessRules() as-is — that call is what actually persists it. */
  updated_channel_business_rules: string;
  /** True when no existing text anchor existed and ORCH fell back to a flow-scoped LLM call. */
  used_llm_fallback: boolean;
  /** Pass through unmodified into confirmRuleFlow()'s request. */
  pre_confirm_hash: string;
}

export interface ConfirmRuleFlowRequest {
  updated_flow: RuleFlow;
  pre_confirm_hash: string;
}

/** Thrown by confirmRuleFlow on a 409 — the flow changed server-side between serialize and
 * confirm (e.g. a concurrent re-parse). Not a generic failure: callers should react by refusing
 * further edits and prompting a re-parse (TDD §5.2/§8.2, RuleFlowCanvas's stale banner), not by
 * retrying the same confirm call. */
export class RuleFlowConfirmConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowConfirmConflictError';
  }
}

/** Thrown by serializeRuleFlow on a 404 — delete-node handover doc §5: the targeted node is
 * already gone (e.g. a concurrent edit deleted it first). Distinguished from a generic Error so
 * a delete diff hitting this during the sequential save loop can clear itself and move on to the
 * next diff, instead of retrying an already-applied delete forever or aborting the whole batch. */
export class RuleFlowNodeNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowNodeNotFoundError';
  }
}

/** Thrown by parseChannelRuleFlows on a 422 — confirmed in shay-email-ml#603: parse returns 422
 * when the channel's business-rules text is empty/whitespace ("nothing to parse"), the single
 * most common cold-start case for this whole feature (any channel whose Workflow tab is opened
 * before rules are configured, reachable independent of "See workflow canvas" per PR #635
 * review M1). Distinguished from a generic Error so the caller can route it to the same
 * friendly "add business rules first" empty state as zero flows, instead of surfacing the raw
 * backend string in an error banner. */
export class RuleFlowParseUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowParseUnavailableError';
  }
}

/** Thrown by updateNodeDescription on a 404 — the channel/flow/node doesn't exist or isn't in the
 * caller's tenant (PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §3.2). Distinguished from a generic
 * Error so callers can show the same "flow no longer exists, reparse" messaging used elsewhere,
 * rather than a raw error banner. */
export class RuleFlowDescriptionNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowDescriptionNotFoundError';
  }
}

/** Thrown by renameFlow on a 404 — the channel/flow no longer exists or isn't in the caller's
 * company (shay-email-backend#564). A Re-load / re-parse replaces flow ids, so a rename racing one
 * lands here; callers should reload the flow list rather than show a raw error. */
export class RuleFlowRenameNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowRenameNotFoundError';
  }
}

/** Thrown by renameFlow on a 409 — another flow in this channel already has that name (BE's
 * duplicate check is case-insensitive and ignores the flow being renamed). Not retryable as-is:
 * the admin has to pick a different name, so the editor stays open with this message. */
export class RuleFlowRenameConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowRenameConflictError';
  }
}

/** Thrown by renameFlow on a 422 — BE rejected the name (empty, over the limit, or invisible/
 * control characters). Distinct from a generic Error so the message can sit under the input. */
export class RuleFlowRenameInvalidError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuleFlowRenameInvalidError';
  }
}

/** shay-email-backend#564 — the display-name limit BE enforces on the whitespace-collapsed name
 * (the DB column itself is wider; 80 is the product limit). */
export const FLOW_NAME_MAX_LENGTH = 80;

/** Same normalisation BE applies before it validates and stores a name — NFC, trim, collapse runs
 * of whitespace — so the frontend's own checks (empty, length, "unchanged") agree with what BE will
 * actually save. */
export function normalizeFlowName(name: string): string {
  return name.normalize('NFC').trim().replace(/\s+/g, ' ');
}

/** Length as BE counts it: characters (code points) of the normalised name, not UTF-16 units.
 * `String.length` counts an emoji as 2, so ~41+ emoji would be rejected here although BE (which
 * allows 80 characters) would accept them. */
export function flowNameLength(name: string): number {
  return Array.from(name).length;
}

function getSessionToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
    ''
  );
}

function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: `Bearer ${getSessionToken()}`,
  };
}

/** GET (BE) — every flow for this channel, full detail. */
export async function listChannelRuleFlows(channelId: string): Promise<RuleFlow[]> {
  const response = await fetch(
    `${API_CONFIG.EMAIL_API_BASE_URL}${CHANNEL_RULE_FLOWS_API_ENDPOINTS.LIST(channelId)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  const data: ListRuleFlowsResponse = await response.json();
  return data.flows;
}

/** GET (BE) — a targeted re-fetch of one flow, not used for the initial load. */
export async function getChannelRuleFlow(channelId: string, flowId: string): Promise<RuleFlow> {
  const response = await fetch(
    `${API_CONFIG.EMAIL_API_BASE_URL}${CHANNEL_RULE_FLOWS_API_ENDPOINTS.GET(channelId, flowId)}`,
    { method: 'GET', headers: authHeaders() }
  );
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/**
 * POST (ORCH) — (re-)parses the channel's current business-rules text into flows, persisting
 * via BE's internal /replace under the hood. Called automatically as part of one continuous
 * loading state when the canvas mounts and flows are missing or stale (TDD §2 row 6), and
 * on-demand via a manual "Re-parse" action thereafter.
 *
 * `force` (PR review roosterhr/shay-email-ml#627, M1): ORCH now skips the LLM call entirely and
 * returns the existing saved flow(s) unchanged whenever the rules text hasn't actually changed
 * since the last successful parse — a real, deliberate optimization, not a bug. But it also means
 * the on-mount auto-parse and the explicit "Re-load" button need different defaults: the
 * automatic call should take the cheap path when nothing changed (force omitted/false, the
 * default), while the button an admin clicks *specifically* to force a fresh derivation — e.g. to
 * pick up an ORCH-side decomposition improvement on an already-parsed channel — must bypass the
 * reuse gate unconditionally, or that action would silently do nothing and the admin would have
 * no way to tell. See ChannelRuleFlowsView.tsx's handleReparse for the one caller that passes
 * `force: true`; loadFlows' own call intentionally leaves it at the default.
 */
export async function parseChannelRuleFlows(channelId: string, force = false): Promise<RuleFlow[]> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${CHANNEL_RULES_PARSE_API_ENDPOINTS.PARSE(channelId, force)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({}) }
  );
  if (response.status === 422) {
    throw new RuleFlowParseUnavailableError(await parseErrorDetail(response));
  }
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  const data: ListRuleFlowsResponse = await response.json();
  return data.flows;
}

/**
 * POST (ORCH) — compute-only, never persists. Step 1 of the edit sequence (TDD §2 rows 9-12):
 * call this first with only the edited node, then pass updated_channel_business_rules into
 * saveChannelBusinessRules(), and only call confirmRuleFlow() if that save succeeds.
 */
export async function serializeRuleFlow(
  channelId: string,
  flowId: string,
  payload: SerializeRuleFlowRequest
): Promise<SerializeRuleFlowResponse> {
  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${CHANNEL_RULES_PARSE_API_ENDPOINTS.SERIALIZE(channelId, flowId)}`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (response.status === 404) {
    throw new RuleFlowNodeNotFoundError(await parseErrorDetail(response));
  }
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/**
 * PUT (BE) — persists the structured flow. Step 3 of the edit sequence: only ever call this
 * after saveChannelBusinessRules() (step 2) has already succeeded with the exact text
 * serializeRuleFlow() (step 1) returned. A 409 means the flow changed server-side since
 * serialize ran — throws RuleFlowConfirmConflictError instead of a generic Error so callers
 * can distinguish "needs re-parse" from an ordinary failure.
 */
export async function confirmRuleFlow(
  channelId: string,
  flowId: string,
  payload: ConfirmRuleFlowRequest
): Promise<void> {
  const response = await fetch(
    `${API_CONFIG.EMAIL_API_BASE_URL}${CHANNEL_RULE_FLOWS_API_ENDPOINTS.CONFIRM(channelId, flowId)}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) }
  );
  if (response.status === 409) {
    throw new RuleFlowConfirmConflictError(await parseErrorDetail(response));
  }
  if (!response.ok) throw new Error(await parseErrorDetail(response));
}

/**
 * PATCH (BE) — persists one node's `description` only (TDD §3.2/§6). Unrelated to the
 * serialize/saveChannelBusinessRules/confirm business-rules sequence above: this never touches
 * edges, recipients, labels, `text`, or `channel_business_rules`, and never calls ORCH. Returns
 * the full updated flow so the caller can resync the canvas from the server's own response
 * (description_source flips to 'user' and description_rules_hash is cleared server-side).
 */
export async function updateNodeDescription(
  channelId: string,
  flowId: string,
  nodeId: string,
  description: string
): Promise<RuleFlow> {
  const response = await fetch(
    `${API_CONFIG.EMAIL_API_BASE_URL}${CHANNEL_RULE_FLOWS_API_ENDPOINTS.UPDATE_NODE_DESCRIPTION(channelId, flowId, nodeId)}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ description }),
    }
  );
  if (response.status === 404) {
    throw new RuleFlowDescriptionNotFoundError(await parseErrorDetail(response));
  }
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}

/** BE's rename 404/409 `detail` is a plain string, but its 422 is FastAPI's schema error: `detail`
 * is a LIST of `{ msg: "Value error, flow_name must ..." }` objects, which the shared
 * parseErrorDetail would reduce to "Request failed (422)". Reads either shape (dropping pydantic's
 * "Value error, " prefix); null when the body has no usable text (the caller picks the fallback). */
async function parseRenameErrorDetail(response: Response): Promise<string | null> {
  const body = await response.json().catch(() => ({} as Record<string, unknown>));
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    for (const item of detail) {
      const msg = (item as { msg?: unknown } | null)?.msg;
      if (typeof msg === 'string' && msg.trim()) return msg.replace(/^Value error,\s*/i, '');
    }
  }
  return null;
}

/**
 * PATCH (BE) — renames one flow: its display name `flow_name` only (shay-email-backend#564).
 * Like updateNodeDescription above, it is unrelated to the serialize/saveChannelBusinessRules/
 * confirm sequence: it never touches nodes, edges, recipients, the business-rules text or the
 * source hash, and never calls ORCH. BE trims and collapses whitespace, enforces 1-80 visible
 * characters, scopes the write to the caller's company and rejects a duplicate name in the channel
 * (case-insensitive). Returns the full updated flow, so callers must show `flow_name` from the
 * response, not the string that was typed — BE may have normalized it.
 *
 * 404 -> RuleFlowRenameNotFoundError (only when the body carries BE's own "No flow ..." message; a
 * bare "Not Found" means the route isn't deployed on this server and is a generic Error instead),
 * 409 -> RuleFlowRenameConflictError, 422 -> RuleFlowRenameInvalidError; anything else is a
 * generic Error (safe to retry).
 */
export async function renameFlow(channelId: string, flowId: string, flowName: string): Promise<RuleFlow> {
  const response = await fetch(
    `${API_CONFIG.EMAIL_API_BASE_URL}${CHANNEL_RULE_FLOWS_API_ENDPOINTS.RENAME_FLOW(channelId, flowId)}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ flow_name: flowName }),
    }
  );
  if (response.status === 404) {
    const detail = await parseRenameErrorDetail(response);
    // A route that doesn't exist answers with FastAPI's bare {"detail": "Not Found"} (or a non-JSON
    // gateway page): the server simply doesn't have the rename route yet (e.g. this frontend reached
    // an environment where shay-email-backend#564 isn't deployed). That is NOT "the flow is gone" —
    // treating it so would show a misleading message and reload the list on every attempt. BE's own
    // 404 carries a specific message ("No flow '...' for channel '...'").
    if (detail && detail.trim().toLowerCase() !== 'not found') throw new RuleFlowRenameNotFoundError(detail);
    throw new Error('Renaming flows is not available on this server yet.');
  }
  if (response.status === 409) {
    throw new RuleFlowRenameConflictError(
      (await parseRenameErrorDetail(response)) ?? 'Another flow in this channel already has that name.'
    );
  }
  if (response.status === 422) {
    throw new RuleFlowRenameInvalidError(
      (await parseRenameErrorDetail(response)) ?? `Enter 1-${FLOW_NAME_MAX_LENGTH} characters of visible text.`
    );
  }
  if (!response.ok) throw new Error(await parseErrorDetail(response));
  return response.json();
}
